// src/App.js
import React, { useState } from "react";
import { Routes, Route, Navigate, NavLink, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./hooks/useAuth";

// Pages
import LoginPage      from "./pages/LoginPage";
import MagicLogin     from "./pages/MagicLogin";
import MyTraining     from "./pages/MyTraining";
import ManageCourses  from "./pages/ManageCourses";
import ManageUsers    from "./pages/ManageUsers";
import ManageCategories from "./pages/ManageCategories";
import RecycleBin     from "./pages/RecycleBin";
import EmailTemplates from "./pages/EmailTemplates";
import UserBin          from "./pages/UserBin";
import Reporting        from "./pages/Reporting";
import BackupRestore    from "./pages/BackupRestore";
import Dashboard        from "./pages/Dashboard";
import MyPolicies      from "./pages/MyPolicies";
import ManagePolicies  from "./pages/ManagePolicies";

const PRIMARY = "#1F2937";

// ── Icons ─────────────────────────────────────────────────────────────────────
const IcoGrad    = <svg viewBox="0 0 20 20" fill="currentColor" width="16" style={{flexShrink:0}}><path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z"/></svg>;
const IcoPeople  = <svg viewBox="0 0 20 20" fill="currentColor" width="16" style={{flexShrink:0}}><path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/></svg>;
const IcoMail    = <svg viewBox="0 0 20 20" fill="currentColor" width="16" style={{flexShrink:0}}><path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/><path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/></svg>;
const IcoDoc     = <svg viewBox="0 0 20 20" fill="currentColor" width="16" style={{flexShrink:0}}><path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd"/></svg>;
const IcoChart   = <svg viewBox="0 0 20 20" fill="currentColor" width="16" style={{flexShrink:0}}><path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zm6-4a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zm6-3a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z"/></svg>;
const IcoBin     = <svg viewBox="0 0 20 20" fill="currentColor" width="16" style={{flexShrink:0}}><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd"/></svg>;
const IcoSettings= <svg viewBox="0 0 20 20" fill="currentColor" width="16" style={{flexShrink:0}}><path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd"/></svg>;
const IcoBuilding= <svg viewBox="0 0 20 20" fill="currentColor" width="16" style={{flexShrink:0}}><path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd"/></svg>;

function NavItemLink({ to, icon, label, onNav }) {
  return (
    <NavLink to={to} className={({isActive})=>`nav-item${isActive?" active":""}`} onClick={onNav}>
      {icon}{label}
    </NavLink>
  );
}
function NavSection({ label }) {
  return <div className="nav-section-label">{label}</div>;
}

// ── Sidebar ───────────────────────────────────────────────────────────────────
function Sidebar({ user, onClose, mobileOpen }) {
  const close = onClose || (()=>{});
  const initials = (user.name||user.email).split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase();
  return (
    <nav className={`sidebar ${mobileOpen?"sidebar--open":""}`}>
      <div className="sidebar-logo">
        <svg viewBox="0 0 20 20" fill="white" width="26"><path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3z"/><path d="M9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0z"/></svg>
        <span>SUFC Online<br/>Training Portal</span>
      </div>
      <div className="sidebar-nav">
        <NavSection label="Training" />
        <NavItemLink to="/my-training"    icon={IcoGrad}    label="My Training"           onNav={close} />
        <NavItemLink to="/my-policies"    icon={IcoDoc}     label="My Policies"            onNav={close} />
        {(user.role==="admin"||user.role==="supervisor") && <>
          <NavSection label="Management" />
          <NavItemLink to="/manage/dashboard" icon={IcoChart}    label="Dashboard"         onNav={close} />
          <NavItemLink to="/manage/courses"   icon={IcoGrad}     label="Manage Courses"    onNav={close} />
          <NavItemLink to="/manage/policies"  icon={IcoDoc}      label="Manage Policies"   onNav={close} />
          <NavItemLink to="/manage/users"     icon={IcoPeople}   label="Manage Users"      onNav={close} />
          <NavItemLink to="/manage/reporting" icon={IcoChart}    label="Reporting"          onNav={close} />
        </>}
        {user.role==="admin" && <>
          <NavSection label="Admin" />
          <NavItemLink to="/admin/categories"  icon={IcoBuilding} label="Categories"       onNav={close} />
          <NavItemLink to="/admin/templates"   icon={IcoMail}     label="Email Templates"   onNav={close} />
          <NavItemLink to="/admin/course-bin"  icon={IcoBin}      label="Course Recycle Bin" onNav={close} />
          <NavItemLink to="/admin/user-bin"    icon={IcoBin}      label="User Recycle Bin"           onNav={close} />
          <NavItemLink to="/admin/backup"      icon={IcoSettings} label="Backup &amp; Restore"   onNav={close} />
        </>}
      </div>
      <div className="sidebar-user">
        <div className="user-avatar" style={{ background:"rgba(255,255,255,0.15)", color:"#fff" }}>{initials}</div>
        <div style={{ flex:1, minWidth:0 }}>
          <div className="user-name">{user.name||user.email}</div>
          <div className="user-upn" style={{ textTransform:"capitalize" }}>{user.role}{user.isExternal?" · External":""}</div>
        </div>
      </div>
    </nav>
  );
}

const PeopleIcon = <svg viewBox="0 0 20 20" fill="currentColor" width="16" style={{flexShrink:0}}><path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z"/></svg>;

// ── App shell ─────────────────────────────────────────────────────────────────
function AppShell() {
  const { user, loading, blocked } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  React.useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:"100vh" }}>
      <span className="spinner" style={{ width:32, height:32 }} />
    </div>
  );

  if (blocked) return (
    <div style={{ minHeight:"100vh", background:"#F3F4F6", display:"flex", alignItems:"center", justifyContent:"center", padding:24, fontFamily:"'Segoe UI',system-ui,sans-serif" }}>
      <div style={{ maxWidth:440, textAlign:"center" }}>
        <div style={{ fontSize:56, marginBottom:16 }}>🔒</div>
        <h2 style={{ fontSize:22, fontWeight:700, color:"#111827", marginBottom:10 }}>Access Restricted</h2>
        <p style={{ fontSize:14, color:"#6B7280", lineHeight:1.7, marginBottom:24 }}>{blocked}</p>
        <a href="/.auth/logout?post_logout_redirect_uri=/" style={{ fontSize:13, color:"#6B7280", textDecoration:"underline" }}>Sign out</a>
      </div>
    </div>
  );

  // Magic login page — no auth needed
  if (location.pathname === "/magic-login") return <MagicLogin />;

  if (!user) return <LoginPage />;

  return (
    <>
      <header className="mobile-header">
        <button className="mobile-menu-btn" onClick={()=>setMobileOpen(o=>!o)}>
          {mobileOpen
            ? <svg viewBox="0 0 20 20" fill="currentColor" width="22"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
            : <svg viewBox="0 0 20 20" fill="currentColor" width="22"><path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd"/></svg>
          }
        </button>
        <div className="mobile-header-title">
          <svg viewBox="0 0 20 20" fill={PRIMARY} width="20"><path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3z"/></svg>
          <span>SUFC Training Portal</span>
        </div>
        <div className="user-avatar" style={{ width:30, height:30, fontSize:11, background:PRIMARY }}>
          {(user.name||user.email).split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase()}
        </div>
      </header>
      {mobileOpen && <div className="mobile-nav-overlay" onClick={()=>setMobileOpen(false)} />}
      <Sidebar user={user} onClose={()=>setMobileOpen(false)} mobileOpen={mobileOpen} />
      <main className="main-content">
        <Routes>
          <Route path="/my-training"    element={<MyTraining />} />
          <Route path="/manage/dashboard"  element={<Dashboard />} />
          <Route path="/manage/courses"   element={<ManageCourses />} />
          <Route path="/my-policies"       element={<MyPolicies />} />
          <Route path="/manage/policies"   element={<ManagePolicies />} />
          <Route path="/manage/users"     element={<ManageUsers />} />
          <Route path="/manage/reporting" element={<Reporting />} />
          <Route path="/admin/categories"  element={<ManageCategories />} />
          <Route path="/admin/templates"   element={<EmailTemplates />} />
          <Route path="/admin/course-bin"  element={<RecycleBin />} />
          <Route path="/admin/user-bin"    element={<UserBin />} />
          <Route path="/admin/backup"      element={<BackupRestore />} />
          <Route path="*"              element={<Navigate to="/my-training" replace />} />
        </Routes>
      </main>
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  );
}
// Sat Jun  6 11:33:15 UTC 2026
